import type { ShootMargin } from '@repo/db/shoot-price'

/**
 * The owner's photography price model, as everything on this side of the wire
 * reads it.
 *
 * ONE type, in `lib`, because four places need the same shape and they are not
 * near each other: the public page quotes from it, the creator estimates from
 * it, the checkout freezes a price with it, and the panel edits it. It lived in
 * `components/sections/admin` while the panel was its only reader; a page
 * importing a type out of the admin folder to price a basket is the wrong
 * direction, so it moved here and the panel imports it back.
 *
 * Money is NET grosze, like everything else priced in this repo.
 */
export type ShootPricingView = {
  rates: {
    session: string
    baseNetMinor: number
    perPhotoNetMinor: number
    includedUnits: number
    extraUnitNetMinor: number
  }[]
  scopes: {
    session: string
    value: string
    label: string
    shortLabel: string
    units: number | null
    /** Non-null only where the session states it — the property's area bands. */
    surchargeNetMinor: number | null
    photos: [number, number, number]
  }[]
  /**
   * What an operator records, and what a day of it costs.
   *
   * Beside the photographer's model rather than in a type of its own: the two
   * answer one question — what does a day of somebody's work cost — and every
   * screen that shows one shows the other. The rates are NULLABLE and are null
   * today; see `schema/recording-types.ts`.
   */
  recordingTypes: {
    slug: string
    label: string
    blurb: string
    icon: string | null
    clientNetMinor: number | null
    performerNetMinor: number | null
    isActive: boolean
  }[]
  margin: ShootMargin
}

/**
 * The two roles whose day is priced by a MODEL rather than by a flat rate, and
 * which model each reads.
 *
 * Slugs, written out, for the reason `REFUNDED_ROLE` in the creator is: this is
 * a client-reachable module and cannot import the catalogue. They are the same
 * slugs `ROLE_KINDS` keys on, and the seed writes them. If a third role ever
 * joins, this stops being a pair of constants and becomes a catalogue column.
 */
export const PHOTOGRAPHER_SLUG = 'fotograf'
export const OPERATOR_SLUG = 'operator'

/** One kind of recording, as the offer and the panel read it. */
export type RecordingTypeView = ShootPricingView['recordingTypes'][number]

/** The row a session lands on, or `undefined` when nothing is sold there. */
export function shootScopeOf(
  model: ShootPricingView | null | undefined,
  session: string,
  value: string,
) {
  return model?.scopes.find((scope) => scope.session === session && scope.value === value)
}

/** The rate card for a session. */
export function shootRateOf(model: ShootPricingView | null | undefined, session: string) {
  return model?.rates.find((rate) => rate.session === session)
}

/** What one kind costs the client, or `null` while nobody has priced it. */
export function recordingRateOf(
  model: ShootPricingView | null | undefined,
  slug: string | null | undefined,
): RecordingTypeView | undefined {
  if (!slug) return undefined
  return model?.recordingTypes.find((type) => type.slug === slug)
}

/**
 * The order the SITE asks the sessions in, which is the order the owner thinks
 * in.
 *
 * The query returns them alphabetically, because a database has no opinion
 * about which session comes first. Every screen that lists them does, and they
 * must all have the SAME opinion — the rates editor and the cooperation offer
 * are read side by side often enough that a different order in each is one more
 * thing to hold in your head while comparing two numbers.
 */
export const SESSION_ORDER = [
  'wizerunkowa',
  'portretowa',
  'kobieca',
  'rodzinna',
  'produktowa',
  'nieruchomosci',
] as const

export const SESSION_LABELS: Record<string, string> = {
  wizerunkowa: 'Sesja wizerunkowa',
  portretowa: 'Sesja portretowa',
  kobieca: 'Sesja kobieca',
  rodzinna: 'Sesja rodzinna',
  produktowa: 'Sesja produktowa',
  nieruchomosci: 'Nieruchomości',
}

/** What the scope column COUNTS — people, products, or square metres. */
export const SESSION_NOUNS: Record<string, string> = {
  wizerunkowa: 'Liczba osób',
  portretowa: 'Liczba osób',
  kobieca: 'Liczba osób',
  rodzinna: 'Liczba osób',
  produktowa: 'Liczba produktów',
  nieruchomosci: 'Powierzchnia',
}

export function sessionLabel(session: string) {
  return SESSION_LABELS[session] ?? session
}

/** Sessions in the owner's order, with anything unrecognised last. */
export function sessionRank(session: string) {
  const index = SESSION_ORDER.indexOf(session as (typeof SESSION_ORDER)[number])
  return index === -1 ? SESSION_ORDER.length : index
}
